import { useEffect, useState, useCallback } from "react";
import { api, BREEDS, BREED_LABELS, fmt, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Warehouse, LogOut, ArrowLeft, Plus, Trash2, Building2, Boxes, PackagePlus, BarChart3, Inbox, ClipboardCheck, CheckCircle2, ArrowDownToLine, ArrowUpFromLine, Pencil } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";

const TABS = [{ k: "balance", label: "Denge Panosu", icon: BarChart3 }, { k: "pending", label: "Gelen Mallar", icon: Inbox }, { k: "stock", label: "Konsolide Stok", icon: Boxes }, { k: "intake", label: "Dış Mal Girişi", icon: PackagePlus }, { k: "count", label: "Günlük Sayım", icon: ClipboardCheck }, { k: "suppliers", label: "Tedarikçiler", icon: Building2 }];

export default function Depot({ onBack }) {
  const { meta } = useApp();
  const { user, logout, hasModule } = useAuth();
  const canDepot = hasModule("depot");
  const dairyId = meta.dairy_supplier_id || "mim_mandira";
  const dairyProducts = meta.dairy_products || [];
  const [dairyQty, setDairyQty] = useState({});
  const [editCount, setEditCount] = useState(null);
  const allItems = [...meta.egg_sizes, ...meta.defects];
  const labelOf = (k) => allItems.find((s) => s.key === k)?.label || k;
  const [tab, setTab] = useState("balance");
  const [stock, setStock] = useState({ mim_kumes: {}, suppliers: [] });
  const [suppliers, setSuppliers] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [pending, setPending] = useState([]);
  const [balance, setBalance] = useState({ today: {}, series: [] });
  const [counts, setCounts] = useState([]);
  const [supForm, setSupForm] = useState({ name: "", contact: "" });
  const [inForm, setInForm] = useState({ supplier_id: "", date: new Date().toISOString().slice(0, 10), waybill_no: "" });
  const [inBreed, setInBreed] = useState("kirmizi");
  const [qtyB, setQtyB] = useState({}); // { breed: { size: val } }
  const [cntSource, setCntSource] = useState("mim");
  const [cntBreed, setCntBreed] = useState("kirmizi");
  const [cntQty, setCntQty] = useState({}); // { breed: { size: val } }

  const load = useCallback(async () => {
    const [s, su, it, pe, ba, co] = await Promise.all([
      api.get("/depot/stock"), api.get("/suppliers"), api.get("/depot/intakes"),
      api.get("/depot/pending"), api.get("/depot/balance"), api.get("/depot/counts"),
    ]);
    setStock(s.data); setSuppliers(su.data); setIntakes(it.data); setPending(pe.data); setBalance(ba.data); setCounts(co.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const confirmArrival = async (id) => { try { await api.post(`/transfers/${id}/confirm`); toast.success("Mal girişi onaylandı, MİM KÜMES stoğuna eklendi"); load(); } catch (e) { toast.error(errMsg(e, "Onay başarısız, tekrar deneyin")); load(); } };

  const saveCount = async () => {
    const items = [];
    Object.entries(cntQty).forEach(([br, sizes]) => Object.entries(sizes).forEach(([size, v]) => { if (v !== "" && v != null) items.push({ breed: br, size, cartons: Number(v || 0) }); }));
    if (!items.length) return toast.error("En az bir kalem girin");
    const body = { date: editCount?.date || new Date().toISOString().slice(0, 10), source: cntSource, items };
    try {
      if (editCount) { await api.put(`/depot/counts/${editCount.id}`, body); toast.success("Sayım güncellendi, stok yeniden ayarlandı"); }
      else { await api.post("/depot/count", body); toast.success("Gün sonu sayımı kaydedildi, stok fiili sayıma eşitlendi"); }
      setCntQty({}); setEditCount(null); load();
    }
    catch (e) { toast.error(errMsg(e)); }
  };
  const startEditCount = (c) => {
    setEditCount(c); setCntSource(c.source);
    setCntQty(JSON.parse(JSON.stringify(c.counted || {})));
    const first = Object.keys(c.counted || {})[0]; if (first) setCntBreed(first);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const delCount = async (c) => {
    if (!window.confirm("Sayım silinsin mi? Sayımın stoka yaptığı düzeltme geri alınır.")) return;
    try { await api.delete(`/depot/counts/${c.id}`); toast.success("Sayım silindi, stok geri alındı"); if (editCount?.id === c.id) { setEditCount(null); setCntQty({}); } load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const addSupplier = async () => {
    if (!supForm.name.trim()) return toast.error("Firma adı girin");
    try { await api.post("/suppliers", supForm); toast.success("Tedarikçi eklendi"); setSupForm({ name: "", contact: "" }); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const delSupplier = async (id) => { await api.delete(`/suppliers/${id}`); load(); };

  const saveIntake = async () => {
    if (!inForm.supplier_id) return toast.error("Tedarikçi seçin");
    const items = [];
    if (inForm.supplier_id === dairyId) {
      Object.entries(dairyQty).forEach(([product, v]) => { const q = Number(String(v).replace(",", ".")); if (q > 0) items.push({ product, qty: q }); });
    } else {
      Object.entries(qtyB).forEach(([br, sizes]) => Object.entries(sizes).forEach(([size, v]) => { if (Number(v) > 0) items.push({ breed: br, size, cartons: Number(v) }); }));
    }
    if (!items.length) return toast.error("En az bir kalem girin");
    try {
      await api.post("/depot/intake", { supplier_id: inForm.supplier_id, date: inForm.date, waybill_no: inForm.waybill_no, items });
      toast.success("Dış mal girişi kaydedildi, depo stoğuna eklendi");
      setQtyB({}); setDairyQty({}); setInForm({ ...inForm, waybill_no: "" }); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const delIntake = async (id) => { await api.delete(`/depot/intakes/${id}`); load(); };

  const StockGroup = ({ title, breeds, accent, testid }) => {
    const hasAny = Object.keys(breeds || {}).length > 0;
    return (
      <div className="rounded-xl border border-border bg-card overflow-hidden mb-4" data-testid={testid}>
        <div className={`px-4 py-3 font-head font-semibold text-white ${accent}`}>{title}</div>
        {!hasAny ? <div className="p-4 text-sm text-muted-foreground">Stok yok.</div> : (
          <div className="p-3 space-y-3">
            {BREEDS.filter((b) => breeds[b]).map((b) => (
              <div key={b}>
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">{BREED_LABELS[b]}</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(breeds[b]).map(([s, v]) => (
                    <div key={s} className="rounded-lg bg-secondary/60 border border-border px-3 py-1.5 text-sm">
                      <span className="text-muted-foreground">{labelOf(s)}:</span> <strong className="tabular-nums">{fmt(v)}</strong>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-20 bg-card border-b border-border px-3 sm:px-4 pb-3 pt-safe-3 flex items-center gap-2 sm:gap-3">
        <button onClick={onBack} data-testid="depot-back" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors shrink-0"><ArrowLeft size={16} /> <span className="hidden sm:inline">Modüller</span></button>
        <div className="flex items-center gap-2 font-head font-semibold min-w-0"><Warehouse size={18} className="text-amber-600 shrink-0" /> <span className="truncate">Ana Sevk & Paketleme Deposu</span></div>
        <div className="ml-auto flex items-center gap-2 sm:gap-3 shrink-0">
          <ThemeToggle testid="depot-theme-toggle" />
          <button onClick={logout} data-testid="depot-logout" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"><LogOut size={15} /> <span className="hidden sm:inline">Çıkış</span></button>
        </div>
      </header>

      <div className="p-4 max-w-4xl mx-auto">
        <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
          {TABS.filter((t) => canDepot || t.k !== "intake").map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`depot-tab-${t.k}`}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === t.k ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "balance" && (
          <div data-testid="depot-balance">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
              <Card title="Günlük Giren" value={balance.today?.in} icon={ArrowDownToLine} accent="text-green-600" testid="bal-in" sub={`Transfer ${fmt(balance.today?.transfer_in || 0)} + Dış ${fmt(balance.today?.intake_in || 0)}`} />
              <Card title="Günlük Çıkan" value={balance.today?.out} icon={ArrowUpFromLine} accent="text-red-600" testid="bal-out" sub={`Sevkiyat ${fmt(balance.today?.shipment_out || 0)} + Satış ${fmt(balance.today?.sales_out || 0)}`} />
              <Card title="Gün Sonu Stok" value={balance.today?.stock_total} icon={Boxes} accent="text-primary" testid="bal-stock" />
              <Card title="Net Değişim" value={balance.today?.net} icon={BarChart3} accent={(balance.today?.net || 0) >= 0 ? "text-green-600" : "text-red-600"} testid="bal-net" signed />
            </div>
            <div className="rounded-xl border border-border bg-card p-4" data-testid="bal-series">
              <div className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground mb-3">Son 7 Gün — Giren / Çıkan (karton)</div>
              <div className="space-y-2">
                {(balance.series || []).map((r) => {
                  const max = Math.max(1, ...(balance.series || []).flatMap((x) => [x.in, x.out]));
                  return (
                    <div key={r.date} className="flex items-center gap-2 text-xs">
                      <span className="w-20 shrink-0 text-muted-foreground">{r.date.slice(5)}</span>
                      <div className="flex-1 flex flex-col gap-0.5">
                        <div className="h-3 rounded bg-green-500/70" style={{ width: `${(r.in / max) * 100}%`, minWidth: r.in ? "8px" : 0 }} title={`Giren ${r.in}`} />
                        <div className="h-3 rounded bg-red-500/70" style={{ width: `${(r.out / max) * 100}%`, minWidth: r.out ? "8px" : 0 }} title={`Çıkan ${r.out}`} />
                      </div>
                      <span className="w-28 shrink-0 text-right tabular-nums">↓{fmt(r.in)} ↑{fmt(r.out)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {tab === "pending" && (
          <div data-testid="depot-pending">
            {pending.length === 0 ? <div className="text-center text-sm text-muted-foreground py-10">Onay bekleyen gelen mal yok.</div> : pending.map((t) => (
              <div key={t.id} className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4 mb-3" data-testid={`pending-${t.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold">{t.from_name || "Kümes"} → {t.to_name}</div>
                  <button onClick={() => confirmArrival(t.id)} data-testid={`confirm-${t.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-4 py-1.5 text-xs font-semibold hover:bg-primary/90 transition-colors"><CheckCircle2 size={14} /> Girişi Onayla</button>
                </div>
                <div className="text-xs text-muted-foreground mb-1">{t.date} · {t.waybill_no || "-"} · Toplam {fmt(t.total)} karton</div>
                <div className="flex flex-wrap gap-1.5">
                  {(t.items || []).map((i, ix) => <span key={ix} className="text-[11px] bg-secondary rounded px-2 py-0.5">{i.is_fire ? "Fire" : BREED_LABELS[i.breed]} {labelOf(i.size)}: {fmt(i.cartons)}</span>)}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "count" && (
          <div className="rounded-xl border border-border bg-card p-5" data-testid="depot-count">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <Fld label="Kaynak / Tedarikçi">
                <select value={cntSource} onChange={(e) => { setCntSource(e.target.value); setCntQty({}); }} disabled={!!editCount} data-testid="count-source" className={cls}>
                  <option value="mim">MİM KÜMES (Kendi Tesisler)</option>
                  {suppliers.filter((s) => s.id !== dairyId).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </Fld>
              {editCount && (
                <div className="flex items-end gap-2 text-sm" data-testid="count-editing">
                  <span className="text-amber-600 font-semibold">Düzenleniyor: {editCount.source_name} · {editCount.date}</span>
                  <button onClick={() => { setEditCount(null); setCntQty({}); }} data-testid="count-edit-cancel" className="text-muted-foreground hover:text-foreground underline">Vazgeç</button>
                </div>
              )}
            </div>
            <div className="flex gap-2 mb-3">
              {BREEDS.map((b) => (
                <button key={b} onClick={() => setCntBreed(b)} data-testid={`count-breed-${b}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${cntBreed === b ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>{BREED_LABELS[b]}</button>
              ))}
            </div>
            <div className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground mb-2">{BREED_LABELS[cntBreed]} · Gün Sonu Fiili Sayım (karton)</div>
            <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-11 border border-border rounded-lg overflow-hidden mb-4">
              {allItems.map((s) => (
                <div key={s.key} className="border-r border-b border-border last:border-r-0">
                  <div className="px-1 py-1 text-[11px] leading-tight font-semibold text-center bg-secondary/60 border-b border-border min-h-[38px] flex items-center justify-center">{s.label}</div>
                  <input type="number" min="0" className="w-full h-8 px-1 text-sm text-center bg-background outline-none" value={cntQty[cntBreed]?.[s.key] ?? ""} onChange={(e) => setCntQty((p) => ({ ...p, [cntBreed]: { ...(p[cntBreed] || {}), [s.key]: e.target.value } }))} data-testid={`count-size-${cntBreed}-${s.key}`} />
                </div>
              ))}
            </div>
            <button onClick={saveCount} data-testid="count-save" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors"><ClipboardCheck size={16} /> {editCount ? "Sayımı Güncelle" : "Gün Sonu Stoğunu Kaydet"}</button>
            {counts.length > 0 && (
              <div className="mt-6">
                <div className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground mb-2">Son Sayımlar (fark: + fazla / − eksik)</div>
                <div className="space-y-2" data-testid="count-list">
                  {counts.slice(0, 15).map((c) => {
                    const diffs = Object.entries(c.diff || {}).flatMap(([br, sz]) => Object.entries(sz).map(([s, d]) => `${BREED_LABELS[br] || br} ${labelOf(s)}: ${d > 0 ? "+" : ""}${d}`));
                    return <div key={c.id} className="rounded-lg border border-border bg-secondary/30 px-3 py-2 text-sm flex items-start gap-2" data-testid={`count-row-${c.id}`}>
                      <div className="flex-1 min-w-0"><strong>{c.source_name}</strong> · {c.date} {diffs.length ? <span className="text-amber-600">· Fark: {diffs.join(", ")}</span> : <span className="text-green-600">· Fark yok</span>}</div>
                      <button onClick={() => startEditCount(c)} data-testid={`count-edit-${c.id}`} title="Düzenle" className="text-muted-foreground hover:text-primary transition-colors p-1"><Pencil size={15} /></button>
                      <button onClick={() => delCount(c)} data-testid={`count-del-${c.id}`} title="Sil" className="text-muted-foreground hover:text-destructive transition-colors p-1"><Trash2 size={15} /></button>
                    </div>;
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "stock" && (
          <div data-testid="depot-stock">
            <StockGroup title="MİM KÜMES (Kendi Tesislerimiz — Konsolide)" breeds={stock.mim_kumes} accent="bg-primary" testid="depot-mim" />
            {stock.suppliers.map((s) => (
              <StockGroup key={s.supplier_id} title={s.name + " (Dış Tedarikçi)"} breeds={s.breeds} accent="bg-amber-600" testid={`depot-sup-${s.supplier_id}`} />
            ))}
            <div className="rounded-xl border border-border bg-card p-5 mb-4" data-testid="depot-dairy">
                <div className="flex items-center gap-2 mb-3"><span className="h-2.5 w-2.5 rounded-full bg-sky-500" /><h3 className="font-head font-semibold">MİM Mandıra (Süt Ürünleri)</h3></div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {dairyProducts.map((p) => ({ ...p, qty: (stock.dairy || []).find((d) => d.key === p.key)?.qty || 0 })).map((d) => (
                    <div key={d.key} className="rounded-lg bg-secondary/50 px-3 py-2" data-testid={`depot-dairy-${d.key}`}>
                      <div className="text-xs text-muted-foreground">{d.label}</div>
                      <div className={`font-semibold tabular-nums ${d.qty ? "" : "text-muted-foreground"}`}>{fmt(d.qty)} {d.unit}</div>
                    </div>
                  ))}
                </div>
              </div>
          </div>
        )}

        {tab === "intake" && canDepot && (
          <div className="rounded-xl border border-border bg-card p-5" data-testid="depot-intake-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <Fld label="Tedarikçi Firma">
                <select value={inForm.supplier_id} onChange={(e) => setInForm({ ...inForm, supplier_id: e.target.value })} data-testid="intake-supplier" className={cls}>
                  <option value="">Seçin</option>{suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </Fld>
              <Fld label="İrsaliye / Fatura No"><input value={inForm.waybill_no} onChange={(e) => setInForm({ ...inForm, waybill_no: e.target.value })} data-testid="intake-waybill" className={cls} /></Fld>
              <Fld label="Tarih"><input type="date" value={inForm.date} onChange={(e) => setInForm({ ...inForm, date: e.target.value })} data-testid="intake-date" className={cls} /></Fld>
            </div>
            {inForm.supplier_id === dairyId ? (
              <>
                <div className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground mb-2">MİM Mandıra · Ürün Giriş Miktarı (yoğurt ve tereyağı adet, diğerleri kg)</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 border border-border rounded-lg overflow-hidden mb-4" data-testid="intake-dairy-grid">
                  {dairyProducts.map((p) => (
                    <div key={p.key} className="border-r border-b border-border">
                      <div className="px-2 py-1 text-[11px] font-semibold text-center bg-secondary/60 border-b border-border">{p.label} <span className="font-normal text-muted-foreground">({p.unit})</span></div>
                      <input type="number" min="0" step={p.unit === "kg" ? "0.01" : "1"} inputMode={p.unit === "kg" ? "decimal" : "numeric"} className="w-full h-9 px-1 text-sm text-center bg-background outline-none"
                        value={dairyQty[p.key] ?? ""} onChange={(e) => setDairyQty((q) => ({ ...q, [p.key]: e.target.value }))} data-testid={`intake-dairy-${p.key}`} />
                    </div>
                  ))}
                </div>
              </>
            ) : (
            <>
            <div className="flex gap-2 mb-3">
              {BREEDS.map((b) => (
                <button key={b} onClick={() => setInBreed(b)} data-testid={`intake-breed-${b}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${inBreed === b ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>{BREED_LABELS[b]}</button>
              ))}
            </div>
            <div className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground mb-2">{BREED_LABELS[inBreed]} · Boy / Gramaj — Giriş Miktarı (koli/viyol)</div>
            <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-11 border border-border rounded-lg overflow-hidden mb-4">
              {allItems.map((s) => (
                <div key={s.key} className="border-r border-b border-border last:border-r-0">
                  <div className="px-1 py-1 text-[11px] leading-tight font-semibold text-center bg-secondary/60 border-b border-border min-h-[38px] flex items-center justify-center">{s.label}</div>
                  <input type="number" min="0" className="w-full h-8 px-1 text-sm text-center bg-background outline-none" value={qtyB[inBreed]?.[s.key] ?? ""} onChange={(e) => setQtyB((p) => ({ ...p, [inBreed]: { ...(p[inBreed] || {}), [s.key]: e.target.value } }))} data-testid={`intake-size-${inBreed}-${s.key}`} />
                </div>
              ))}
            </div>
            </>
            )}
            <button onClick={saveIntake} data-testid="intake-save" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors"><Plus size={16} /> Mal Girişini Kaydet</button>

            {intakes.length > 0 && (
              <div className="mt-6">
                <div className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground mb-2">Son Girişler</div>
                <div className="space-y-2" data-testid="intake-list">
                  {intakes.slice(0, 20).map((it) => (
                    <div key={it.id} className="flex items-center justify-between rounded-lg border border-border bg-secondary/30 px-3 py-2 text-sm" data-testid={`intake-row-${it.id}`}>
                      <span><strong>{it.supplier_name}</strong> · {it.date} · {it.kind === "dairy" ? (it.items || []).map((x) => `${x.label} ${fmt(x.qty)} ${x.unit}`).join(", ") : `${fmt(it.total)} koli`} {it.waybill_no ? `· ${it.waybill_no}` : ""}</span>
                      <button onClick={() => delIntake(it.id)} data-testid={`intake-del-${it.id}`} className="text-muted-foreground hover:text-destructive transition-colors"><Trash2 size={15} /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "suppliers" && (
          <div className="rounded-xl border border-border bg-card p-5" data-testid="depot-suppliers">
            {canDepot ? (
            <div className="flex flex-col sm:flex-row gap-2 mb-4">
              <input placeholder="Firma adı (örn. Coşkun Yumurta)" value={supForm.name} onChange={(e) => setSupForm({ ...supForm, name: e.target.value })} data-testid="supplier-name" className={cls + " flex-1"} />
              <input placeholder="İletişim (opsiyonel)" value={supForm.contact} onChange={(e) => setSupForm({ ...supForm, contact: e.target.value })} data-testid="supplier-contact" className={cls + " flex-1"} />
              <button onClick={addSupplier} data-testid="supplier-add" className="inline-flex items-center justify-center gap-1.5 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors"><Plus size={15} /> Ekle</button>
            </div>
            ) : <div className="text-xs text-muted-foreground mb-4" data-testid="supplier-readonly-note">Tedarikçi eklemek/silmek için "Ana Depo Stoğu" yetkisi gerekir.</div>}
            {suppliers.length === 0 ? <div className="text-sm text-muted-foreground py-6 text-center">Henüz tedarikçi eklenmedi.</div> : (
              <div className="space-y-2" data-testid="supplier-list">
                {suppliers.map((s) => (
                  <div key={s.id} className="flex items-center justify-between rounded-lg border border-border bg-secondary/30 px-3 py-2.5" data-testid={`supplier-row-${s.id}`}>
                    <div><strong className="text-sm">{s.name}</strong>{s.contact ? <span className="text-xs text-muted-foreground ml-2">{s.contact}</span> : null}</div>
                    {canDepot && s.id !== dairyId && <button onClick={() => delSupplier(s.id)} data-testid={`supplier-del-${s.id}`} className="text-muted-foreground hover:text-destructive transition-colors"><Trash2 size={16} /></button>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const cls = "h-9 rounded-md border border-input bg-background px-2 text-sm focus:ring-2 focus:ring-primary outline-none";
const Card = ({ title, value, icon: Icon, accent, testid, sub, signed }) => (
  <div className="rounded-xl border border-border bg-card p-4" data-testid={testid}>
    <div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">{title}</span><Icon size={16} className={accent} /></div>
    <div className={`text-2xl font-bold tabular-nums ${accent}`}>{signed && (value || 0) > 0 ? "+" : ""}{fmt(value || 0)}</div>
    {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
  </div>
);
const Fld = ({ label, children }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-1.5">{label}</label>
    {children}
  </div>
);
